import type { Session, SessionUser } from "@/types";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ApiError, apiFetch } from "@/services/api";
import { useCartStore } from "@/components/providers/cart-store";
import { useWishlistStore } from "@/components/providers/wishlist-store";

/**
 * Replaces `next-auth/react`.
 *
 * The session is no longer managed by a Next.js route handler: the NestJS API
 * exposes the equivalent endpoints under `/api/auth/*`, and the httpOnly
 * `mova.session-token` cookie is the single source of truth. The exported names,
 * the `status` tri-state and the return shapes are all identical to NextAuth's,
 * so `useSession()`, `signIn()`, `signOut()` and `update()` are called exactly
 * as before.
 *
 * Endpoints used:
 *   GET  /api/auth/session   → `{ user, expires }` or `null`
 *   POST /api/auth/login     → sets the cookie, returns the session
 *   POST /api/auth/google    → verifies Google ID token, sets cookie, returns session
 *   POST /api/auth/logout    → clears the cookie
 */

type SessionStatus = "loading" | "authenticated" | "unauthenticated";

type SignInResult = {
  error?: string;
  ok?: boolean;
  status?: number;
  url?: string;
  role?: string;
  requiresLinking?: boolean;
  email?: string;
};

type UpdateArgs = { name?: string | null; email?: string | null; image?: string | null };

type AuthContextValue = {
  data: Session | null;
  session: Session | null;
  status: SessionStatus;
  update: (args?: UpdateArgs) => Promise<Session | null>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/** Broadcast so every `useSession()` consumer refreshes after a sign-in/out. */
const SESSION_EVENT = "mova-session-changed";

function broadcastSessionChange(action: "update" | "clear") {
  window.dispatchEvent(new CustomEvent(SESSION_EVENT, { detail: { action } }));
}

export function SessionProvider({
  children,
  /** NextAuth accepted this to skip the initial fetch; kept for compatibility. */
  session: initialSession = null,
}: {
  children: ReactNode;
  session?: Session | null;
}) {
  const [session, setSession] = useState<Session | null>(initialSession);
  const [status, setStatus] = useState<SessionStatus>(
    initialSession ? "authenticated" : "loading",
  );
  const mounted = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const data = await apiFetch<Session | null>("/api/auth/session", { method: "GET" });
      setSession(data ?? null);
      setStatus(data?.user ? "authenticated" : "unauthenticated");
      return data ?? null;
    } catch {
      // A failed session probe means "not signed in", which is what NextAuth
      // surfaced to the UI when the endpoint errored.
      setSession(null);
      setStatus("unauthenticated");
      return null;
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void refresh();

    // Sign-in and sign-out happen in another tab/component; keep every tab in
    // sync the way NextAuth's `useSession` did with its storage event.
    const onFocus = () => void refresh();
    const onStorage = (event: StorageEvent) => {
      if (event.key === "mova-session") void refresh();
    };
    const onSessionChange = (event: Event) => {
      const detail = (event as CustomEvent<{ action: string }>).detail;
      if (!detail || detail.action !== "update") return;
      void refresh();
    };

    window.addEventListener("focus", onFocus);
    window.addEventListener("storage", onStorage);
    window.addEventListener(SESSION_EVENT, onSessionChange);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SESSION_EVENT, onSessionChange);
    };
  }, [refresh]);

  const update = useCallback(
    async (args: UpdateArgs = {}) => {
      const next = await refresh();
      if (!next?.user) return null;
      return { ...next, user: { ...next.user, ...args } };
    },
    [refresh],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ data: session, session, status, update }),
    [session, status, update],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useSession(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useSession must be used inside a <SessionProvider>.");
  }
  return context;
}

type CredentialSignIn = {
  email: string;
  password: string;
  redirect?: boolean;
  callbackUrl?: string;
};

export type GoogleTokenSignIn = {
  idToken?: string;
  credential?: string;
  linkPassword?: string;
  accountType?: "CUSTOMER" | "VENDOR";
  businessName?: string;
  phone?: string;
  vendorTypeId?: string;
  address?: string;
  redirect?: boolean;
  callbackUrl?: string;
};

/**
 * `signIn("credentials", { … })` posts to `/api/auth/login`;
 * `signIn("google", { idToken: "…" })` posts to `/api/auth/google` to verify GIS ID token;
 * standard OAuth redirects straight to `/api/auth/${provider}`.
 */
export async function signIn(
  provider: "credentials" | "google" | "github",
  options: CredentialSignIn | GoogleTokenSignIn | { callbackUrl?: string; redirect?: boolean } = {},
): Promise<SignInResult> {
  const callbackUrl =
    "callbackUrl" in options && typeof options.callbackUrl === "string"
      ? options.callbackUrl
      : "/dashboard";

  // Google ID Token verification flow (Google Identity Services)
  if (provider === "google" && ("idToken" in options || "credential" in options)) {
    const { idToken, credential, linkPassword, accountType, businessName, phone, vendorTypeId, address } = options as GoogleTokenSignIn;
    try {
      const session = await apiFetch<Session>("/api/auth/google", {
        method: "POST",
        body: { idToken, credential, linkPassword, accountType, businessName, phone, vendorTypeId, address },
      });

      window.localStorage.setItem("mova-session", String(Date.now()));
      broadcastSessionChange("update");

      if (session?.user?.id) {
        return { ok: true, status: 200, role: session.user.role };
      }
      return { error: "Failed to authenticate with Google." };
    } catch (error) {
      if (error instanceof TypeError && !("status" in error)) {
        return { error: "Backend not connected. Please try again later." };
      }
      if (error instanceof ApiError) {
        const body = error.body as { requiresLinking?: boolean; email?: string; message?: string } | null;
        if (body && typeof body === "object" && body.requiresLinking) {
          return {
            error: error.message,
            status: error.status,
            requiresLinking: true,
            email: body.email,
          };
        }
      }
      const message = error instanceof Error ? error.message : "Google authentication failed.";
      return { error: message };
    }
  }

  if (provider !== "credentials") {
    if (typeof window !== "undefined") {
      window.location.href = `/api/auth/${provider}?callbackUrl=${encodeURIComponent(callbackUrl)}`;
    }
    return { ok: true };
  }

  const { email, password } = options as CredentialSignIn;

  try {
    const session = await apiFetch<Session>("/api/auth/login", {
      method: "POST",
      body: { email, password },
    });

    // Let every other `useSession()` consumer pick up the new cookie.
    window.localStorage.setItem("mova-session", String(Date.now()));
    broadcastSessionChange("update");

    if (session?.user?.id) {
      return { ok: true, status: 200, role: session.user.role };
    }
    return { error: "Invalid email or password." };
  } catch (error) {
    // Network-level failure (backend unreachable) — fetch throws a TypeError
    // with no HTTP status. Distinguish it from a real 401 so the user gets a
    // useful message instead of "Invalid email or password."
    if (error instanceof TypeError && !("status" in error)) {
      return { error: "Backend not connected. Please try again later." };
    }
    const message = error instanceof Error ? error.message : "Invalid email or password.";
    return { error: message };
  }
}

export async function signOut(options: { callbackUrl?: string } = {}): Promise<SignInResult> {
  try {
    await apiFetch("/api/auth/logout", { method: "POST" });
  } catch {
    // Even if the call failed, clear local state so the UI is not stuck.
  }

  try {
    useCartStore.getState().clearCart();
    useWishlistStore.setState({ items: [], wishlistOwnerId: null });
  } catch {
    // ignore
  }

  try {
    window.localStorage.removeItem("mova-session");
    window.localStorage.removeItem("mova-ethiopia-cart");
    window.localStorage.removeItem("mova-ethiopia-wishlist");
    window.localStorage.removeItem("mova_checkout_draft");
    window.localStorage.removeItem("mova_last_used_address");
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const key = window.localStorage.key(i);
      if (
        key &&
        (key.startsWith("mova_checkout_draft") ||
          key.startsWith("mova_last_used_address") ||
          key.startsWith("mova-ethiopia-cart") ||
          key.startsWith("mova-ethiopia-wishlist"))
      ) {
        window.localStorage.removeItem(key);
      }
    }
  } catch {
    // ignore
  }
  broadcastSessionChange("clear");

  const callbackUrl = options.callbackUrl ?? "/";
  if (typeof window !== "undefined") {
    window.location.href = callbackUrl;
  }

  return { ok: true };
}

export type { Session, SessionStatus, SessionUser };